import type { RefObject, Dispatch, SetStateAction } from 'react'
import { PROCUREMENT_STATE_LABELS } from '@crm/shared'
import type { RequestItemProcurementState } from '@crm/shared'
import { Loader } from '../../../shared/ui/Loader'
import { QueueFilterBtn } from './QueueFilterBtn'
import type { ClientCategory, RequestItemPriority } from '../../../shared/api/types'
import type { useDashboardQueueLink } from '../useDashboardQueueLink'

/** Значения селекта «Статус закупщика» (серверный procState). */
const PROC_STATE_OPTIONS: RequestItemProcurementState[] = [
  'NEW',
  'REQUESTED',
  'WAITING',
  'NEEDS_INFO',
  'QUOTED',
  'NOT_FOUND',
  'NO_RESPONSE',
  'REFUSED',
]

/** Sticky-тулбар очереди квот: заголовок + быстрые фильтры + поиск +
 *  панель действий + кнопка «Загрузить ответ». Вынесен из
 *  ProcurementQueuePage.tsx (рефакторинг 21.05.2026), разметка/поведение
 *  без изменений. */
export function QueueToolbar({
  topBlockRef,
  activeCount,
  inWorkCount,
  processedCount,
  selectedCount,
  // серверные фильтры «дашборд РОЗ → очередь» (URL, useDashboardQueueLink)
  dash,
  // фильтры
  assignedToMe,
  setAssignedToMe,
  inQueue,
  setInQueue,
  overdue,
  setOverdue,
  burning,
  setBurning,
  stale,
  setStale,
  needsInfo,
  setNeedsInfo,
  rework,
  setRework,
  category,
  setCategory,
  priority,
  setPriority,
  procStateFilter: _procStateFilter,
  setProcStateFilter,
  anyFilterActive,
  // поиск
  searchText,
  setSearchText,
  sortActive,
  resetSort,
  // действия
  onDownloadRequest,
  onDeselectAll,
  uploadRef,
  isUploading,
  uploadResult,
  onUploadResponse,
}: {
  topBlockRef: RefObject<HTMLDivElement>
  activeCount: number
  inWorkCount: number
  processedCount: number
  selectedCount: number
  dash: ReturnType<typeof useDashboardQueueLink>
  assignedToMe: boolean
  setAssignedToMe: Dispatch<SetStateAction<boolean>>
  inQueue: boolean
  setInQueue: Dispatch<SetStateAction<boolean>>
  overdue: boolean
  setOverdue: Dispatch<SetStateAction<boolean>>
  burning: boolean
  setBurning: Dispatch<SetStateAction<boolean>>
  stale: boolean
  setStale: Dispatch<SetStateAction<boolean>>
  needsInfo: boolean
  setNeedsInfo: Dispatch<SetStateAction<boolean>>
  rework: boolean
  setRework: Dispatch<SetStateAction<boolean>>
  category: ClientCategory | ''
  setCategory: Dispatch<SetStateAction<ClientCategory | ''>>
  priority: RequestItemPriority | ''
  setPriority: Dispatch<SetStateAction<RequestItemPriority | ''>>
  procStateFilter: 'NEW' | 'ACTIVE' | null
  setProcStateFilter: Dispatch<SetStateAction<'NEW' | 'ACTIVE' | null>>
  anyFilterActive: boolean
  searchText: string
  setSearchText: Dispatch<SetStateAction<string>>
  sortActive: boolean
  resetSort: () => void
  onDownloadRequest: () => void
  onDeselectAll: () => void
  uploadRef: RefObject<HTMLInputElement>
  isUploading: boolean
  uploadResult: string | null
  onUploadResponse: (e: React.ChangeEvent<HTMLInputElement>) => void
}) {
  return (
    // Верхний блок (заголовок + фильтры + поиск + действия) прилипает
    // к верху main-контейнера при скролле страницы. Отрицательные
    // margin-x/-top поднимают блок в padding main, чтобы фон был вплотную
    // к шапке с курсами валют — без зазора.
    <div
      ref={topBlockRef}
      className="sticky top-0 z-20 bg-gray-50 space-y-3 -mx-3 lg:-mx-6 px-3 lg:px-6 pt-3 pb-2 -mt-3 lg:-mt-6"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold text-gray-900">Очередь квот</h1>
        {/* При фильтре с дашборда сравнивают с плиткой → показываем СУММУ по
            всем секциям, а не только верхнюю (16.07). */}
        {dash.active ? (
          <span className="text-sm text-gray-500">
            найдено всего:{' '}
            <span className="font-semibold text-gray-900">
              {activeCount + inWorkCount + processedCount}
            </span>
            <span className="text-gray-400">
              {' '}
              (новые {activeCount} · в работе {inWorkCount} · обработано {processedCount})
            </span>
          </span>
        ) : (
          <span className="text-sm text-gray-400">
            {activeCount} позиций
            {inWorkCount > 0 && ` · в работе: ${inWorkCount}`}
            {processedCount > 0 && ` · обработано: ${processedCount}`}
          </span>
        )}
      </div>

      {/* Быстрые серверные фильтры */}
      <div className="flex flex-wrap gap-2 items-center bg-white rounded-xl border border-gray-200 px-3 py-2.5">
        <QueueFilterBtn
          label="Мои"
          active={assignedToMe}
          onChange={() => setAssignedToMe(!assignedToMe)}
        />
        <QueueFilterBtn
          label="Новые (очередь)"
          active={inQueue}
          onChange={() => setInQueue(!inQueue)}
        />
        <QueueFilterBtn
          label="Просрочено 🔴"
          active={overdue}
          onChange={() => setOverdue(!overdue)}
        />
        <QueueFilterBtn label="Горящие 🔥" active={burning} onChange={() => setBurning(!burning)} />
        <QueueFilterBtn label="Без движения 🕔" active={stale} onChange={() => setStale(!stale)} />
        <QueueFilterBtn
          label="Нужна инфо"
          active={needsInfo}
          onChange={() => setNeedsInfo(!needsInfo)}
        />
        <QueueFilterBtn label="Доработка" active={rework} onChange={() => setRework(!rework)} />

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as ClientCategory | '')}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">Все кат.</option>
          <option value="A">A — Ключевой заказчик</option>
          <option value="B">B — Растущий клиент</option>
          <option value="C">C — Новый заказчик</option>
        </select>

        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value as RequestItemPriority | '')}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">Все приор.</option>
          <option value="HIGH">🔴 Высокий</option>
          <option value="MEDIUM">🟡 Средний</option>
          <option value="LOW">⚪ Низкий</option>
        </select>

        {/* Серверные фильтры дашборда РОЗ: точный статус закупщика +
            «Отказ (цена 0)» + «В счёт». Состояние живёт в URL (deep-link). */}
        <select
          value={dash.filters.procState ?? ''}
          onChange={(e) => dash.setParam('procState', e.target.value || null)}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          title="Точный статус закупщика (серверный фильтр)"
        >
          <option value="">Статус закупщика</option>
          {PROC_STATE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {PROCUREMENT_STATE_LABELS[s]}
            </option>
          ))}
        </select>
        <QueueFilterBtn
          label="Отказ (цена 0)"
          active={dash.filters.zeroPrice}
          onChange={() => dash.setParam('zeroPrice', dash.filters.zeroPrice ? null : '1')}
        />
        <QueueFilterBtn
          label="В счёт"
          active={dash.filters.wentToInvoice}
          onChange={() => dash.setParam('wentToInvoice', dash.filters.wentToInvoice ? null : '1')}
        />

        {anyFilterActive && (
          <button
            onClick={() => {
              setAssignedToMe(false)
              setInQueue(false)
              setOverdue(false)
              setBurning(false)
              setStale(false)
              setNeedsInfo(false)
              setRework(false)
              setCategory('')
              setPriority('')
              setSearchText('')
              setProcStateFilter(null)
            }}
            className="text-xs text-gray-400 hover:text-gray-700"
          >
            × Сбросить
          </button>
        )}
      </div>

      {/* Плашка активного фильтра с дашборда РОЗ (deep-link) */}
      {dash.active && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2">
          <span className="text-sm text-blue-700">
            Фильтр с дашборда: <span className="font-medium">{dash.label}</span>
          </span>
          <button onClick={dash.reset} className="text-xs text-blue-500 hover:text-blue-700">
            × Сбросить
          </button>
        </div>
      )}

      {/* Поиск над таблицей */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="text"
          placeholder="Поиск по таблице..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white flex-1 min-w-[200px] max-w-md"
        />
        {searchText && (
          <button
            onClick={() => setSearchText('')}
            className="text-xs text-gray-400 hover:text-gray-700"
            title="Очистить поиск"
          >
            × Очистить
          </button>
        )}
        {sortActive && (
          <button
            onClick={resetSort}
            className="text-xs px-2 py-1 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50"
            title="Вернуть стандартный порядок (горит → приоритет → категория → дедлайн)"
          >
            ↺ Стандартный порядок
          </button>
        )}
      </div>

      {/* Панель действий с выделенными строками */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 bg-brand-50 border border-brand-200 rounded-xl px-4 py-2.5">
          <span className="text-sm text-brand-700 font-medium">Выбрано: {selectedCount}</span>
          <button
            onClick={onDownloadRequest}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Сформировать запрос
          </button>
          <button onClick={onDeselectAll} className="text-xs text-gray-500 hover:text-gray-700">
            Отменить
          </button>
        </div>
      )}

      {/* Загрузить ответ от поставщика */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => uploadRef.current?.click()}
          disabled={isUploading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {isUploading ? (
            <>
              <Loader variant="ring" size={18} />
              Квоты загружаются…
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              Загрузить ответ
            </>
          )}
        </button>
        <input
          ref={uploadRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={onUploadResponse}
        />
        {/* Заметная плашка процесса — чтобы закупщик не жал загрузку повторно. */}
        {isUploading && (
          <span className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
            <span className="w-3.5 h-3.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            Квоты загружаются, не закрывайте страницу…
          </span>
        )}
        {!isUploading && uploadResult && (
          <span
            className={`text-sm ${uploadResult.startsWith('Ошибка') ? 'text-red-600' : 'text-green-600'}`}
          >
            {uploadResult}
          </span>
        )}
      </div>
    </div>
  )
}
